import React, { ReactElement, useEffect } from "react";
import {
    ExternalForm,
    useListExternalFormsQuery,
} from "../../../__generated__/api";
import Select, { Option, SelectInstanceHandler } from "../../select/Select";

export interface SelectedValue {
    id: string;
    hasChildren: boolean;
}

export interface ExternalFormsPickerProps {
    id?: string;
    placeholder?: string;
    className?: string;
    isClearable: boolean;
    value?: string;
    selectedValue?: string;
    onSelected: (value: SelectedValue | undefined) => void;
}

const ExternalFormPicker = React.forwardRef(({
    onSelected, placeholder, value, className, isClearable, id, selectedValue,
}: ExternalFormsPickerProps, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useListExternalFormsQuery({
        skip: value === "" || id === "",
        fetchPolicy: "network-only",
        variables: {
            id,
            value,
        },
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const values = data?.externalForms?.map(form => ({
        label: form.name as string,
        value: form.id.toString() as string,
        data: form,
    })) ?? [];
    const selected = values.find(val => val.data.isSelected)
            ?? (selectedValue ? values.find(val => val.value === selectedValue) : undefined);

    useEffect(() => {
        if (selected) {
            onSelected({
                id: selected?.data.id,
                hasChildren: selected?.data.hasChildren ?? false,
            });
        }
    }, [selected?.value]);

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }
    const handleSelected = (selectedForm: Option<ExternalForm>) => {
        if (!selectedForm) {
            onSelected(undefined);
            return;
        }
        onSelected({
            id: selectedForm.data?.id,
            hasChildren: selectedForm.data?.hasChildren as boolean,
        });
    };

    return (
        <Select
            ref={ref}
            className={className}
            placeholder={placeholder}
            onChange={handleSelected}
            options={(values ?? []) as Option<ExternalForm>[]}
            value={selected as Option<ExternalForm>}
            isClearable={isClearable}
        />
    );
});

ExternalFormPicker.defaultProps = {

    placeholder: undefined,

    className: "",
    id: undefined,
    selectedValue: undefined,

    value: undefined,
};

export default ExternalFormPicker;
